import { useEffect, useMemo, useRef, useState } from 'react';
import {
  applySuccessfulAssociationUpdates,
  getAssociationUpdatePlan,
  getAudioAssociations,
  getCurrentAssociatedAudioIdsForDevice,
  getInitialAssociatedAudio,
  getStagedAssociationChanges,
  getToggledAssociationChanges,
  reconcileAssociationChanges,
} from '../utils/audioManager.utils';

const useAudioAssociations = ({
  audioList,
  deviceIds,
  deviceOptions,
  group,
  managerMode,
  scopeKey,
}) => {
  const [changes, setChanges] = useState({ draftChanges: {}, savedChanges: {} });
  const [selectedAudioId, setSelectedAudioId] = useState(null);
  const [hasInitializedSelection, setHasInitializedSelection] = useState(false);
  const audioListRef = useRef(audioList);
  audioListRef.current = audioList;

  useEffect(() => {
    setChanges({ draftChanges: {}, savedChanges: {} });
    setSelectedAudioId(null);
    setHasInitializedSelection(false);
  }, [scopeKey]);

  useEffect(() => {
    setChanges((current) => reconcileAssociationChanges(current, audioList));
  }, [audioList]);

  useEffect(() => {
    if (hasInitializedSelection) return;
    const initialAudio = getInitialAssociatedAudio(audioList, deviceIds);
    if (initialAudio) {
      setSelectedAudioId(initialAudio.id);
      setHasInitializedSelection(true);
    }
  }, [audioList, deviceIds, hasInitializedSelection]);

  const savedAudioList = useMemo(
    () => getAudioAssociations(audioList, changes.savedChanges),
    [audioList, changes.savedChanges],
  );
  const draftAudioList = useMemo(
    () => getAudioAssociations(savedAudioList, changes.draftChanges),
    [savedAudioList, changes.draftChanges],
  );
  const selectedAudio = draftAudioList.find((audio) => audio.id === selectedAudioId);
  const selectedAudios = selectedAudio ? [selectedAudio.id] : [];
  const selectedDevices = selectedAudio?.device_ids || [];

  const updatePlan = useMemo(
    () =>
      getAssociationUpdatePlan(
        savedAudioList,
        draftAudioList,
        deviceOptions,
        group && managerMode === 'audio' ? deviceIds || [] : [],
        selectedAudioId,
      ),
    [savedAudioList, draftAudioList, deviceOptions, group, managerMode, deviceIds, selectedAudioId],
  );

  const onAudioSelectToggle = (event, audioId) => {
    setSelectedAudioId(event.target.checked ? audioId : null);
    setHasInitializedSelection(true);
  };

  const toggleManagedAudioForDevice = (deviceId, audioId) => {
    setChanges((current) =>
      getToggledAssociationChanges(current, savedAudioList, deviceId, audioId),
    );
  };

  const toggleAssociatedDevice = (deviceId) => {
    if (!selectedAudio || group) return;
    toggleManagedAudioForDevice(deviceId, selectedAudioId);
  };

  const getEffectiveManagedAudioIdsForDevice = (deviceId) =>
    getCurrentAssociatedAudioIdsForDevice(draftAudioList, deviceId);

  const stageUpdates = (updates) => {
    setChanges((current) => getStagedAssociationChanges(current, updates));
  };

  const applySuccessfulUpdates = (updates) => {
    setChanges((current) =>
      reconcileAssociationChanges(
        applySuccessfulAssociationUpdates(current, updates),
        audioListRef.current,
      ),
    );
  };

  return {
    selectedAudios,
    selectedDevices,
    draftAudioList,
    updatePlan,
    onAudioSelectToggle,
    toggleAssociatedDevice,
    toggleManagedAudioForDevice,
    getEffectiveManagedAudioIdsForDevice,
    stageUpdates,
    applySuccessfulUpdates,
  };
};

export default useAudioAssociations;
